import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AuthGate } from "@/components/AuthGate";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getReview } from "@/lib/reviews.functions";
import { Button } from "@/components/ui/button";
import { ProcessingIndicator } from "@/components/ProcessingIndicator";
import { ReviewFloatingNav } from "@/components/ReviewFloatingNav";
import {
  CheckCircle2,
  ShieldAlert,
  HelpCircle,
  Sparkles,
  ListChecks,
  ChevronDown,
  Trophy,
  FileText,
  ClipboardCheck,
  Highlighter,
  AlertTriangle,
  BookOpen,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { PageContainer } from "@/components/PageContainer";

export const Route = createFileRoute("/reviews_/$reviewId/hybrid")({
  head: () => ({
    meta: [
      { title: "Review (Hybrid) — Tiger's Eye" },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: () => (
    <AuthGate>
      <HybridReview />
    </AuthGate>
  ),
});

type ReviewResult = {
  ghostwriting_refusal?: string;
  overall_judgement?: string;
  what_works_well?: string[];
  main_risks_or_weaknesses?: string[];
  category_critique?: { category: string; comments: string }[];
  socratic_questions?: string[];
  errors_and_omissions?: string[];
  referencing_and_bibliography_issues?: { issue: string; evidence: string; fix: string }[];
  academic_integrity_risks?: { risk: string; why: string; suggested_action: string }[];
  priority_revision_plan?: {
    must_fix_before_submission?: string[];
    should_fix_to_improve_quality?: string[];
    could_improve_polish?: string[];
  };
  highest_impact_next_steps?: string[];
  assumptions_made?: string[];
  post_marking?: Record<string, unknown> | null;
};

/**
 * Semantic tone palette for the hybrid layout.
 * Held local to this route so it does not touch the global token system.
 *  - positive  → green   (strengths, wins)
 *  - attention → amber   (improvements, risks of weakness)
 *  - critical  → red     (integrity, must-fix)
 *  - reflection→ purple  (Socratic / questions to consider)
 *  - scholarly → existing token, used as a neutral accent
 */
type Tone = "positive" | "attention" | "critical" | "reflection" | "scholarly";

const TONE_HEX: Record<Tone, string> = {
  positive: "#16a34a", // green-600
  attention: "#d97706", // amber-600
  critical: "#dc2626", // red-600
  reflection: "#7c3aed", // violet-600
  scholarly: "var(--tone-scholarly)",
};

function toneColor(t: Tone) {
  return TONE_HEX[t];
}

function toneSoft(t: Tone) {
  return `color-mix(in oklch, ${TONE_HEX[t]} 12%, transparent)`;
}

function LayoutSwitcher({ reviewId, current }: { reviewId: string; current: "hybrid" | "classic" }) {
  return (
    <div
      role="tablist"
      aria-label="Review layout"
      className="inline-flex rounded-md border border-border bg-secondary p-1 gap-1 mb-6"
    >
      <Link
        to="/reviews/$reviewId"
        params={{ reviewId }}
        role="tab"
        aria-selected={current === "classic"}
        className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${
          current === "classic"
            ? "bg-primary text-primary-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground"
        }`}
      >
        Classic
      </Link>
      <Link
        to="/reviews/$reviewId/hybrid"
        params={{ reviewId }}
        role="tab"
        aria-selected={current === "hybrid"}
        className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${
          current === "hybrid"
            ? "bg-primary text-primary-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground"
        }`}
      >
        Hybrid
      </Link>
    </div>
  );
}

function HybridReview() {
  const { reviewId } = Route.useParams();
  const fetchFn = useServerFn(getReview);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["review", reviewId],
    queryFn: () => fetchFn({ data: { id: reviewId } }),
    refetchInterval: (q) => {
      const status = (q.state.data?.review as { status?: string } | undefined)?.status;
      return status === "pending" || status === "streaming" ? 2500 : false;
    },
  });

  if (isLoading)
    return <PageContainer className="text-ink-tertiary">Loading review…</PageContainer>;
  if (!data) return <PageContainer>Not found.</PageContainer>;

  const review = data.review as {
    id: string;
    status: string;
    intake: Record<string, string>;
    result: ReviewResult | null;
    tokens_used: number | null;
    created_at: string;
    draft_text: string | null;
  };
  const intake = review.intake ?? {};
  const result = review.result ?? {};
  const title = intake.module || intake.course || intake.assessment_type || "Untitled review";

  if (review.status === "pending" || review.status === "streaming") {
    return (
      <PageContainer>
        <LayoutSwitcher reviewId={reviewId} current="hybrid" />
        <p className="overline mb-3 text-center">Working</p>
        <h1 className="font-serif text-4xl text-primary text-center">
          Tiger's Eye is reading your draft…
        </h1>
        <div className="mt-10">
          <ProcessingIndicator
            estimatedSeconds={60}
            stages={[
              "Parsing your draft…",
              "Retrieving relevant academic norms…",
              "Checking referencing and integrity signals…",
              "Drafting critique segments…",
              "Composing the editorial review…",
              "Finalising…",
            ]}
          />
        </div>
        <div className="mt-8 flex justify-center">
          <Button variant="outline" onClick={() => refetch()}>
            Refresh now
          </Button>
        </div>
      </PageContainer>
    );
  }

  if (review.status === "failed") {
    return (
      <PageContainer>
        <LayoutSwitcher reviewId={reviewId} current="hybrid" />
        <p className="overline mb-3 text-destructive">Failed</p>
        <h1 className="font-serif text-4xl text-primary mb-4">Review didn't generate</h1>
        <pre className="text-sm text-ink-secondary whitespace-pre-wrap bg-paper p-4 border border-border">
          {(result as { error?: string }).error ?? "Unknown error"}
        </pre>
      </PageContainer>
    );
  }

  // -----------------------------------------------------------------
  // Derived data
  // -----------------------------------------------------------------

  const counts = {
    integrity: result.academic_integrity_risks?.length ?? 0,
    mustFix: result.priority_revision_plan?.must_fix_before_submission?.length ?? 0,
    shouldFix: result.priority_revision_plan?.should_fix_to_improve_quality?.length ?? 0,
    polish: result.priority_revision_plan?.could_improve_polish?.length ?? 0,
    risks: result.main_risks_or_weaknesses?.length ?? 0,
    errors: result.errors_and_omissions?.length ?? 0,
    refs: result.referencing_and_bibliography_issues?.length ?? 0,
    questions: result.socratic_questions?.length ?? 0,
    strengths: result.what_works_well?.length ?? 0,
  };

  const banner = computeBanner(result, counts);

  // SmartWins: lead with positives and most actionable next steps (3–5 items).
  const smartWins: { tone: Tone; icon: React.ReactNode; text: string }[] = [];
  for (const w of (result.what_works_well ?? []).slice(0, 2)) {
    smartWins.push({ tone: "positive", icon: <CheckCircle2 className="h-5 w-5" />, text: w });
  }
  for (const n of (result.highest_impact_next_steps ?? []).slice(0, 3)) {
    if (smartWins.length >= 5) break;
    smartWins.push({ tone: "attention", icon: <Sparkles className="h-5 w-5" />, text: n });
  }
  while (smartWins.length < 3 && (result.what_works_well?.length ?? 0) > smartWins.filter((s) => s.tone === "positive").length) {
    const idx = smartWins.filter((s) => s.tone === "positive").length;
    const more = result.what_works_well?.[idx];
    if (!more) break;
    smartWins.push({ tone: "positive", icon: <CheckCircle2 className="h-5 w-5" />, text: more });
  }

  // Inline risk highlighting: extract evidence snippets from
  // referencing + integrity arrays. These are short verbatim phrases the
  // model already lifts from the draft, so a simple highlight pass works.
  const riskSnippets: { text: string; tone: Tone }[] = [];
  for (const r of result.referencing_and_bibliography_issues ?? []) {
    if (r.evidence && r.evidence.length > 4 && r.evidence.length < 200) {
      riskSnippets.push({ text: r.evidence, tone: "attention" });
    }
  }
  for (const r of result.academic_integrity_risks ?? []) {
    if (r.why && r.why.length > 4 && r.why.length < 200) {
      riskSnippets.push({ text: r.why, tone: "critical" });
    }
  }

  return (
    <PageContainer className="animate-fade-in">
      <LayoutSwitcher reviewId={reviewId} current="hybrid" />

      <header className="mb-8">
        <p className="overline mb-2">Editorial review · Hybrid view</p>
        <h1 className="font-serif text-4xl md:text-5xl text-primary mb-2">{title}</h1>
        <p className="text-sm text-ink-tertiary">
          {intake.university && `${intake.university} · `}
          {intake.year_of_study && `${intake.year_of_study} · `}
          {new Date(review.created_at).toLocaleString("en-GB")}
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
        {/* LEFT COLUMN */}
        <div className="min-w-0">
          {result.ghostwriting_refusal && (
            <div
              className="border-l-2 pl-4 mb-6"
              style={{ borderColor: toneColor("critical") }}
            >
              <p className="overline mb-2" style={{ color: toneColor("critical") }}>
                Refusal
              </p>
              <p className="text-ink">{result.ghostwriting_refusal}</p>
            </div>
          )}

          {/* Top: Readiness meter + SmartWins */}
          <section
            aria-labelledby="readiness-heading"
            className="mb-6 border border-border bg-card p-6"
            style={{ borderLeft: `4px solid ${toneColor(banner.tone)}` }}
          >
            <ReadinessBanner banner={banner} counts={counts} />
            {result.overall_judgement && (
              <p className="mt-4 font-serif text-base leading-relaxed text-ink-secondary">
                {result.overall_judgement}
              </p>
            )}
          </section>

          {smartWins.length > 0 && (
            <section
              aria-label="Smart wins"
              className="mb-8 border border-border bg-card p-6"
              style={{ borderLeft: `4px solid ${toneColor("positive")}` }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span
                  className="flex h-10 w-10 items-center justify-center rounded-md"
                  style={{ color: toneColor("positive"), backgroundColor: toneSoft("positive") }}
                >
                  <Trophy className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="font-serif text-xl text-primary leading-tight">Smart wins</h2>
                  <p className="text-xs text-ink-tertiary mt-0.5">
                    Where you stand and what to do next
                  </p>
                </div>
              </div>
              <ul className="space-y-3">
                {smartWins.map((w, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span
                      className="shrink-0 mt-0.5"
                      style={{ color: toneColor(w.tone) }}
                      aria-hidden
                    >
                      {w.icon}
                    </span>
                    <span className="text-ink leading-relaxed">{w.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Folder stack in the requested priority order */}
          <div className="space-y-3">
            <FolderStack result={result} counts={counts} />

            {result.post_marking && (
              <PostMarkingFolder data={result.post_marking} />
            )}
          </div>

          <div className="border-t border-border pt-6 mt-10 flex flex-wrap items-center justify-between gap-3 text-sm text-ink-tertiary">
            <span>{review.tokens_used ? `${review.tokens_used} tokens used` : ""}</span>
            <Link to="/reviews">
              <Button variant="outline" size="sm">
                Back to reviews
              </Button>
            </Link>
          </div>
        </div>

        {/* RIGHT COLUMN — draft preview with inline highlights (desktop) */}
        <aside className="hidden lg:block lg:sticky lg:top-6 lg:self-start">
          <DraftPreview draft={review.draft_text} snippets={riskSnippets} />
        </aside>
      </div>

      {/* Mobile: floating button opens drawer with the same draft preview */}
      <div className="lg:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button
              size="lg"
              className="fixed bottom-4 right-4 z-40 shadow-lg gap-2 rounded-full"
              aria-label="Open draft preview with highlights"
            >
              <Highlighter className="h-4 w-4" />
              Draft
              {riskSnippets.length > 0 && (
                <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-foreground/20 px-1.5 text-xs font-medium">
                  {riskSnippets.length}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[85vh] overflow-y-auto">
            <SheetHeader className="text-left">
              <SheetTitle>Your draft</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <DraftPreview draft={review.draft_text} snippets={riskSnippets} />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <ReviewFloatingNav
        onExpandAll={() => window.dispatchEvent(new CustomEvent("review:expand-all"))}
        onCollapseAll={() => window.dispatchEvent(new CustomEvent("review:collapse-all"))}
      />
    </PageContainer>
  );
}

// ===================================================================
// Readiness banner — qualitative, derived only from model signals.
// No invented numeric grade. Mirrors the chip row used by Classic so
// the two views give the same emotional read of the draft.
// ===================================================================

type Banner = {
  tone: Tone;
  headline: string;
  detail?: string;
};

function computeBanner(
  result: ReviewResult,
  c: { integrity: number; mustFix: number },
): Banner {
  if (result.ghostwriting_refusal) {
    return {
      tone: "critical",
      headline: "Refusal issued",
      detail: "See the refusal note below.",
    };
  }
  if (c.integrity > 0) {
    return {
      tone: "attention",
      headline: "Review integrity points before submitting",
      detail: `${c.integrity} integrity point${c.integrity === 1 ? "" : "s"} to review`,
    };
  }
  if (c.mustFix > 0) {
    return {
      tone: "attention",
      headline: "Address must-fix items before submitting",
      detail: `${c.mustFix} must-fix item${c.mustFix === 1 ? "" : "s"}`,
    };
  }
  return {
    tone: "positive",
    headline: "On track",
    detail: "No blocking issues flagged.",
  };
}

function ReadinessBanner({
  banner,
  counts,
}: {
  banner: Banner;
  counts: {
    strengths: number;
    risks: number;
    mustFix: number;
    integrity: number;
    refs: number;
  };
}) {
  const color = toneColor(banner.tone);
  return (
    <div>
      <h2 id="readiness-heading" className="overline mb-2">
        Editorial readiness
      </h2>
      <p className="font-serif text-2xl leading-tight" style={{ color }}>
        {banner.headline}
      </p>
      {banner.detail && (
        <p className="mt-1 text-sm text-ink-tertiary">{banner.detail}</p>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <ChipPill tone="positive" icon={<CheckCircle2 className="h-3.5 w-3.5" />}
          label={`${counts.strengths} strength${counts.strengths === 1 ? "" : "s"}`}
          onClick={() => openFolder("strengths")} />
        <ChipPill tone="attention" icon={<AlertTriangle className="h-3.5 w-3.5" />}
          label={`${counts.risks} risk${counts.risks === 1 ? "" : "s"}`}
          onClick={() => openFolder("improvements")} />
        {counts.mustFix > 0 && (
          <ChipPill tone="attention" icon={<ListChecks className="h-3.5 w-3.5" />}
            label={`${counts.mustFix} must-fix`}
            onClick={() => openFolder("improvements")} />
        )}
        {counts.integrity > 0 && (
          <ChipPill tone="attention" icon={<ShieldAlert className="h-3.5 w-3.5" />}
            label={`${counts.integrity} integrity`}
            onClick={() => openFolder("integrity")} />
        )}
        {counts.refs > 0 && (
          <ChipPill tone="scholarly" icon={<BookOpen className="h-3.5 w-3.5" />}
            label={`${counts.refs} referencing`}
            onClick={() => openFolder("improvements")} />
        )}
      </div>
    </div>
  );
}

function openFolder(id: string) {
  window.dispatchEvent(new CustomEvent("review:open-folder", { detail: { id } }));
  setTimeout(() => {
    const el = document.getElementById(`folder-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, 50);
}

function ChipPill({ tone, icon, label, onClick }: { tone: Tone; icon: React.ReactNode; label: string; onClick?: () => void }) {
  const style = {
    color: toneColor(tone),
    borderColor: toneColor(tone),
    backgroundColor: toneSoft(tone),
  };
  const className = "inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border";
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${className} cursor-pointer transition hover:scale-[1.03]`} style={style}>
        {icon}
        {label}
      </button>
    );
  }
  return (
    <span className={className} style={style}>
      {icon}
      {label}
    </span>
  );
}


// ===================================================================
// Folder stack
// ===================================================================

function FolderStack({ result, counts }: { result: ReviewResult; counts: Record<string, number> }) {
  const folders: {
    id: string;
    tone: Tone;
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    count: number;
    defaultOpen?: boolean;
    render: () => React.ReactNode;
  }[] = [];

  // 1. Integrity Risks (red)
  if (counts.integrity > 0 && result.academic_integrity_risks) {
    folders.push({
      id: "integrity",
      tone: "critical",
      icon: <ShieldAlert className="h-6 w-6" />,
      title: "Integrity points",
      subtitle: "Review before submitting",
      count: counts.integrity,
      defaultOpen: false,
      render: () => (
        <div className="space-y-3">
          {result.academic_integrity_risks!.map((r, i) => (
            <div key={i} className="border-l-2 pl-4" style={{ borderColor: toneColor("critical") }}>
              <p className="font-medium text-ink">{r.risk}</p>
              {r.why && <p className="text-sm text-ink-secondary mt-1">{r.why}</p>}
              {r.suggested_action && (
                <p className="text-sm text-ink-tertiary mt-1">
                  <em>Suggested action:</em> {r.suggested_action}
                </p>
              )}
            </div>
          ))}
        </div>
      ),
    });
  }

  // 2. Areas for Improvement (amber) — combine plan + risks + errors + refs
  const improvementCount =
    counts.mustFix + counts.shouldFix + counts.polish + counts.risks + counts.errors + counts.refs;
  if (improvementCount > 0) {
    folders.push({
      id: "improvements",
      tone: "attention",
      icon: <ListChecks className="h-6 w-6" />,
      title: "Areas for improvement",
      subtitle: "Prioritised by impact",
      count: improvementCount,
      defaultOpen: false,
      render: () => (
        <div className="space-y-5">
          {result.priority_revision_plan && (
            <>
              <TrafficGroup
                tone="critical"
                label="Must fix before submission"
                items={result.priority_revision_plan.must_fix_before_submission}
              />
              <TrafficGroup
                tone="attention"
                label="Should fix to improve"
                items={result.priority_revision_plan.should_fix_to_improve_quality}
              />
              <TrafficGroup
                tone="scholarly"
                label="Could improve for polish"
                items={result.priority_revision_plan.could_improve_polish}
              />
            </>
          )}

          {result.main_risks_or_weaknesses && result.main_risks_or_weaknesses.length > 0 && (
            <div>
              <SubHeading tone="attention" label={`Risks & weaknesses (${counts.risks})`} />
              <BulletList items={result.main_risks_or_weaknesses} tone="attention" />
            </div>
          )}

          {result.errors_and_omissions && result.errors_and_omissions.length > 0 && (
            <div>
              <SubHeading tone="attention" label={`Errors & omissions (${counts.errors})`} />
              <BulletList items={result.errors_and_omissions} tone="attention" />
            </div>
          )}

          {result.referencing_and_bibliography_issues &&
            result.referencing_and_bibliography_issues.length > 0 && (
              <div>
                <SubHeading tone="scholarly" label={`Referencing (${counts.refs})`} />
                <div className="space-y-2">
                  {result.referencing_and_bibliography_issues.map((r, i) => (
                    <div key={i} className="border border-border bg-paper p-3">
                      <p className="font-medium text-primary text-sm">{r.issue}</p>
                      {r.evidence && (
                        <p className="text-xs text-ink-tertiary mt-1">
                          <em>Evidence:</em> {r.evidence}
                        </p>
                      )}
                      {r.fix && (
                        <p className="text-xs text-ink-secondary mt-1">
                          <em>Fix:</em> {r.fix}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          {result.category_critique && result.category_critique.length > 0 && (
            <div>
              <SubHeading tone="scholarly" label={`By category (${result.category_critique.length})`} />
              <div className="space-y-3">
                {result.category_critique.map((c, i) => (
                  <div
                    key={i}
                    className="border-l-2 pl-4"
                    style={{ borderColor: toneColor("scholarly") }}
                  >
                    <p className="font-medium text-primary text-sm">{c.category}</p>
                    <p className="text-sm text-ink-secondary mt-1">{c.comments}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ),
    });
  }

  // 3. Questions to Consider (purple — reflection)
  if (counts.questions > 0 && result.socratic_questions) {
    folders.push({
      id: "questions",
      tone: "reflection",
      icon: <HelpCircle className="h-6 w-6" />,
      title: "Questions to consider",
      subtitle: "Sharpen your thinking",
      count: counts.questions,
      render: () => <BulletList items={result.socratic_questions!} tone="reflection" />,
    });
  }

  // 4. Strengths (green) — full list (SmartWins already showed top items)
  if (counts.strengths > 0 && result.what_works_well) {
    folders.push({
      id: "strengths",
      tone: "positive",
      icon: <CheckCircle2 className="h-6 w-6" />,
      title: "Strengths",
      subtitle: "Keep doing these",
      count: counts.strengths,
      render: () => <BulletList items={result.what_works_well!} tone="positive" />,
    });
  }

  return (
    <>
      {folders.map((f) => (
        <Folder key={f.id} folder={f} />
      ))}
    </>
  );
}

function Folder({
  folder,
}: {
  folder: {
    id: string;
    tone: Tone;
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    count: number;
    defaultOpen?: boolean;
    render: () => React.ReactNode;
  };
}) {
  const [open, setOpen] = useState(!!folder.defaultOpen);
  useEffect(() => {
    const expand = () => setOpen(true);
    const collapse = () => setOpen(false);
    const openIfMatch = (e: Event) => {
      const detail = (e as CustomEvent<{ id: string }>).detail;
      if (detail?.id === folder.id) setOpen(true);
    };
    window.addEventListener("review:expand-all", expand);
    window.addEventListener("review:collapse-all", collapse);
    window.addEventListener("review:open-folder", openIfMatch);
    return () => {
      window.removeEventListener("review:expand-all", expand);
      window.removeEventListener("review:collapse-all", collapse);
      window.removeEventListener("review:open-folder", openIfMatch);
    };
  }, [folder.id]);
  const color = toneColor(folder.tone);
  return (
    <div
      id={`folder-${folder.id}`}
      className="border border-border bg-card overflow-hidden scroll-mt-24"
      style={{ borderLeft: `4px solid ${color}` }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`folder-body-${folder.id}`}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-secondary/40 transition-colors"
      >
        <span
          className="flex h-12 w-12 items-center justify-center rounded-md shrink-0"
          style={{ color, backgroundColor: toneSoft(folder.tone) }}
          aria-hidden
        >
          {folder.icon}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-serif text-lg text-primary leading-tight">{folder.title}</span>
          {folder.subtitle && (
            <span className="block text-xs text-ink-tertiary mt-0.5">{folder.subtitle}</span>
          )}
        </span>
        <span
          className="text-xs font-mono px-2 py-0.5 rounded-full shrink-0"
          style={{ color, backgroundColor: toneSoft(folder.tone) }}
        >
          {folder.count}
        </span>
        <ChevronDown
          className={`h-5 w-5 text-ink-tertiary shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open && (
        <div id={`folder-body-${folder.id}`} className="px-5 pb-6 pt-1">
          {folder.render()}
        </div>
      )}
    </div>
  );
}

function PostMarkingFolder({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) => v != null && v !== "");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const expand = () => setOpen(true);
    const collapse = () => setOpen(false);
    window.addEventListener("review:expand-all", expand);
    window.addEventListener("review:collapse-all", collapse);
    return () => {
      window.removeEventListener("review:expand-all", expand);
      window.removeEventListener("review:collapse-all", collapse);
    };
  }, []);
  if (entries.length === 0) return null;
  const color = toneColor("scholarly");
  return (
    <div
      className="border border-border bg-card overflow-hidden"
      style={{ borderLeft: `4px solid ${color}` }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-secondary/40 transition-colors"
      >
        <span
          className="flex h-12 w-12 items-center justify-center rounded-md shrink-0"
          style={{ color, backgroundColor: toneSoft("scholarly") }}
          aria-hidden
        >
          <ClipboardCheck className="h-6 w-6" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-serif text-lg text-primary leading-tight">
            Post-marking comparison
          </span>
          <span className="block text-xs text-ink-tertiary mt-0.5">
            How predictions compared to the marker
          </span>
        </span>
        <ChevronDown
          className={`h-5 w-5 text-ink-tertiary shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open && (
        <div className="px-5 pb-6 pt-1 space-y-2 text-sm">
          {entries.map(([k, v]) => (
            <div key={k} className="border-l-2 pl-3" style={{ borderColor: color }}>
              <p className="font-medium text-primary capitalize">{k.replace(/_/g, " ")}</p>
              <p className="text-ink-secondary whitespace-pre-wrap">
                {typeof v === "string" ? v : JSON.stringify(v, null, 2)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================================================================
// Draft preview with inline highlighting
// ===================================================================

function DraftPreview({
  draft,
  snippets,
}: {
  draft: string | null;
  snippets: { text: string; tone: Tone }[];
}) {
  const segments = useMemo(() => highlightDraft(draft ?? "", snippets), [draft, snippets]);
  const highlightCount = segments.filter((s) => s.tone).length;

  return (
    <div className="border border-border bg-paper">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <FileText className="h-4 w-4 text-ink-tertiary" />
        <p className="overline">Your draft</p>
        {highlightCount > 0 && (
          <span className="ml-auto text-xs text-ink-tertiary">
            {highlightCount} highlight{highlightCount === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <div className="max-h-[72vh] overflow-y-auto overflow-x-hidden px-4 py-3">
        {draft ? (
          <pre className="whitespace-pre-wrap break-words font-serif text-sm leading-relaxed text-ink-secondary m-0 max-w-full">
            {segments.map((seg, i) =>
              seg.tone ? (
                <mark
                  key={i}
                  title={seg.tone === "critical" ? "Possible integrity flag" : "Referencing flag"}
                  style={{
                    backgroundColor: toneSoft(seg.tone),
                    color: toneColor(seg.tone),
                    padding: "0 2px",
                    borderRadius: 2,
                    borderBottom: `1px solid ${toneColor(seg.tone)}`,
                  }}
                >
                  {seg.text}
                </mark>
              ) : (
                <span key={i}>{seg.text}</span>
              ),
            )}
          </pre>
        ) : (
          <p className="text-sm text-ink-tertiary italic">Draft text not available.</p>
        )}
      </div>
      {highlightCount > 0 && (
        <div className="border-t border-border px-4 py-2 flex items-center gap-4 text-xs text-ink-tertiary">
          <LegendDot tone="attention" label="Referencing" />
          <LegendDot tone="critical" label="Integrity" />
        </div>
      )}
    </div>
  );
}

function LegendDot({ tone, label }: { tone: Tone; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block w-2 h-2 rounded-full"
        style={{ backgroundColor: toneColor(tone) }}
      />
      {label}
    </span>
  );
}

/**
 * Split the draft into plain text + highlighted snippet segments.
 * Snippets are matched case-insensitively, first occurrence wins.
 * Critical (integrity) snippets are applied before attention (referencing)
 * so they take precedence when they overlap.
 */
function highlightDraft(
  draft: string,
  snippets: { text: string; tone: Tone }[],
): { text: string; tone?: Tone }[] {
  if (!draft || snippets.length === 0) return [{ text: draft }];

  type Range = { start: number; end: number; tone: Tone };
  const ranges: Range[] = [];
  const ordered = [...snippets].sort((a, b) => (a.tone === "critical" ? -1 : 1));
  const lower = draft.toLowerCase();

  for (const s of ordered) {
    const needle = s.text.trim().toLowerCase();
    if (needle.length < 5) continue;
    const idx = lower.indexOf(needle);
    if (idx === -1) continue;
    const end = idx + needle.length;
    // Skip if overlaps an existing range
    if (ranges.some((r) => !(end <= r.start || idx >= r.end))) continue;
    ranges.push({ start: idx, end, tone: s.tone });
  }

  if (ranges.length === 0) return [{ text: draft }];
  ranges.sort((a, b) => a.start - b.start);

  const out: { text: string; tone?: Tone }[] = [];
  let cursor = 0;
  for (const r of ranges) {
    if (r.start > cursor) out.push({ text: draft.slice(cursor, r.start) });
    out.push({ text: draft.slice(r.start, r.end), tone: r.tone });
    cursor = r.end;
  }
  if (cursor < draft.length) out.push({ text: draft.slice(cursor) });
  return out;
}

// ===================================================================
// Small shared bits
// ===================================================================

function BulletList({ items, tone }: { items: string[]; tone: Tone }) {
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 text-ink-secondary">
          <span className="font-mono text-xs mt-1" style={{ color: toneColor(tone) }} aria-hidden>
            ▸
          </span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function SubHeading({ tone, label }: { tone: Tone; label: string }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: toneColor(tone) }}
        aria-hidden
      />
      <p className="text-xs font-medium uppercase tracking-wider" style={{ color: toneColor(tone) }}>
        {label}
      </p>
    </div>
  );
}

function TrafficGroup({ tone, label, items }: { tone: Tone; label: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <SubHeading tone={tone} label={`${label} (${items.length})`} />
      <ul className="space-y-1 pl-4">
        {items.map((it, i) => (
          <li key={i} className="text-ink-secondary list-disc">
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
